import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, FileText, Calendar as CalendarIcon, Headphones, MessageSquare, Mail } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminContentCalendar = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const { token } = useAuth();

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const monthName = currentDate.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const [articlesRes, eventsRes, podcastRes, socialRes, newslettersRes] = await Promise.all([
          fetch(`${API_URL}/api/cms/articles?limit=100`, { headers }),
          fetch(`${API_URL}/api/cms/events?limit=100`, { headers }),
          fetch(`${API_URL}/api/cms/podcast?limit=100`, { headers }),
          fetch(`${API_URL}/api/cms/social?limit=100`, { headers }),
          fetch(`${API_URL}/api/cms/newsletters?limit=100`, { headers }),
        ]);
        
        const allItems = [];
        
        if (articlesRes.ok) {
          const d = await articlesRes.json();
          d.articles.forEach(a => {
            const date = (a.publish_at || a.created_at || '').slice(0, 10);
            if (date) allItems.push({ date, type: 'article', title: a.title, status: a.status, icon: FileText, color: 'bg-blue-500' });
          });
        }
        if (eventsRes.ok) {
          const d = await eventsRes.json();
          d.events.forEach(e => {
            const date = (e.event_date || e.created_at || '').slice(0, 10);
            if (date) allItems.push({ date, type: 'event', title: e.title, status: e.status, icon: CalendarIcon, color: 'bg-emerald-500' });
          });
        }
        if (podcastRes.ok) {
          const d = await podcastRes.json();
          d.episodes.forEach(e => {
            const date = (e.date || e.created_at || '').slice(0, 10);
            if (date) allItems.push({ date, type: 'podcast', title: e.title, status: e.status, icon: Headphones, color: 'bg-purple-500' });
          });
        }
        if (socialRes.ok) {
          const d = await socialRes.json();
          d.posts.forEach(p => {
            const date = (p.schedule_at || p.created_at || '').slice(0, 10);
            if (date) allItems.push({ date, type: 'social', title: (p.content || '').slice(0, 40) + '...', status: p.status, icon: MessageSquare, color: 'bg-pink-500' });
          });
        }
        if (newslettersRes.ok) {
          const d = await newslettersRes.json();
          d.newsletters.forEach(n => {
            const date = (n.schedule_at || n.created_at || '').slice(0, 10);
            if (date) allItems.push({ date, type: 'newsletter', title: n.subject, status: n.status, icon: Mail, color: 'bg-amber-500' });
          });
        }
        
        setItems(allItems);
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    fetchAll();
  }, []);

  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));
  const today = () => setCurrentDate(new Date());

  const getItemsForDay = (day) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return items.filter(i => i.date === dateStr);
  };

  const isToday = (day) => {
    const now = new Date();
    return day === now.getDate() && month === now.getMonth() && year === now.getFullYear();
  };

  const days = [];
  // Empty cells for days before month starts
  for (let i = 0; i < (firstDay === 0 ? 6 : firstDay - 1); i++) {
    days.push(null);
  }
  for (let d = 1; d <= daysInMonth; d++) {
    days.push(d);
  }

  // Legend
  const legend = [
    { type: 'article', label: 'Articles', color: 'bg-blue-500' },
    { type: 'event', label: 'Events', color: 'bg-emerald-500' },
    { type: 'podcast', label: 'Podcast', color: 'bg-purple-500' },
    { type: 'social', label: 'Social', color: 'bg-pink-500' },
    { type: 'newsletter', label: 'Newsletter', color: 'bg-amber-500' },
  ];

  // Upcoming items (next 7 days)
  const now = new Date();
  const upcoming = items
    .filter(i => { const d = new Date(i.date); return d >= now && d <= new Date(now.getTime() + 7 * 86400000); })
    .sort((a, b) => a.date.localeCompare(b.date))
    .slice(0, 8);

  return (
    <div data-testid="admin-content-calendar">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-serif text-2xl text-cream font-semibold">Content Calendar</h1>
          <p className="text-cream/30 text-sm mt-1">All scheduled content in one view</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={prevMonth} className="p-2 text-cream/30 hover:text-gold transition-colors"><ChevronLeft size={18} /></button>
          <button onClick={today} className="px-3 py-1.5 text-xs text-cream/40 border border-cream/10 rounded hover:border-gold/20 hover:text-gold transition-all">Today</button>
          <span className="text-cream font-serif text-lg font-semibold min-w-[160px] text-center">{monthName}</span>
          <button onClick={nextMonth} className="p-2 text-cream/30 hover:text-gold transition-colors"><ChevronRight size={18} /></button>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 mb-6">
        {legend.map((l) => (
          <div key={l.type} className="flex items-center gap-1.5 text-xs text-cream/30">
            <div className={`w-2 h-2 rounded-full ${l.color}`} />
            {l.label}
          </div>
        ))}
      </div>

      {loading ? <div className="py-16 text-center text-cream/20">Loading calendar...</div> : (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Calendar Grid */}
          <div className="lg:col-span-3">
            <div className="bg-navy border border-gold/[0.06] rounded-xl overflow-hidden">
              {/* Day headers */}
              <div className="grid grid-cols-7 border-b border-gold/[0.04]">
                {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => (
                  <div key={d} className="px-2 py-2.5 text-[10px] uppercase tracking-wider text-cream/20 text-center font-medium">{d}</div>
                ))}
              </div>
              {/* Days */}
              <div className="grid grid-cols-7">
                {days.map((day, i) => {
                  const dayItems = day ? getItemsForDay(day) : [];
                  return (
                    <div key={i} className={`min-h-[90px] border-b border-r border-gold/[0.03] p-1.5 ${day ? 'hover:bg-white/[0.01]' : 'bg-navy-deep/30'} transition-colors`}>
                      {day && (
                        <>
                          <span className={`text-xs font-mono block mb-1 ${isToday(day) ? 'text-gold font-bold' : 'text-cream/25'}`}>{day}</span>
                          <div className="space-y-0.5">
                            {dayItems.slice(0, 3).map((item, j) => (
                              <div key={j} className={`flex items-center gap-1 px-1 py-0.5 rounded text-[9px] ${item.color}/10 group cursor-default`} title={item.title}>
                                <div className={`w-1.5 h-1.5 rounded-full ${item.color} flex-shrink-0`} />
                                <span className="text-cream/40 truncate">{item.title}</span>
                              </div>
                            ))}
                            {dayItems.length > 3 && <span className="text-[8px] text-cream/15 px-1">+{dayItems.length - 3} more</span>}
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Sidebar — Upcoming */}
          <div>
            <div className="bg-navy border border-gold/[0.06] rounded-xl p-4">
              <h3 className="text-cream text-sm font-semibold mb-3">Upcoming (7 days)</h3>
              {upcoming.length === 0 ? (
                <p className="text-cream/15 text-xs py-4 text-center">Nothing scheduled</p>
              ) : (
                <div className="space-y-2">
                  {upcoming.map((item, i) => {
                    const ItemIcon = item.icon;
                    return (
                      <div key={i} className="flex items-start gap-2 py-2 border-b border-gold/[0.03] last:border-0">
                        <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 ${item.color}/10`}>
                          <ItemIcon size={10} className="text-cream/40" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-cream/50 text-xs truncate">{item.title}</p>
                          <div className="flex items-center gap-1.5 mt-0.5">
                            <span className="text-cream/15 text-[9px] font-mono">{item.date.slice(5)}</span>
                            <span className={`text-[8px] px-1 py-0.5 rounded ${item.status === 'published' ? 'bg-green-500/10 text-green-400' : item.status === 'scheduled' ? 'bg-amber-500/10 text-amber-300' : 'bg-cream/[0.03] text-cream/20'}`}>{item.status}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Content counts */}
            <div className="bg-navy border border-gold/[0.06] rounded-xl p-4 mt-4">
              <h3 className="text-cream text-sm font-semibold mb-3">This Month</h3>
              <div className="space-y-2">
                {legend.map((l) => {
                  const monthStr = `${year}-${String(month + 1).padStart(2, '0')}`;
                  const count = items.filter(i => i.type === l.type && i.date.startsWith(monthStr)).length;
                  return (
                    <div key={l.type} className="flex items-center justify-between text-xs">
                      <span className="text-cream/30 flex items-center gap-1.5"><div className={`w-1.5 h-1.5 rounded-full ${l.color}`} />{l.label}</span>
                      <span className="text-cream/50 font-mono">{count}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminContentCalendar;
